// kitchen/web/src/lib/table-board.ts
// Pure functions for the "per-table" KDS board view (feature 084).
// All functions are dependency-free and testable in node env.
//
// Feature 298 — the texts a cook or a waiter reads (group names, table and
// takeaway names, the "Entré" block, the wave label) come from the caller's
// translator `t`; without one they are the Italian source.

import { sourceT, type SharedT } from './source-text';

// ─── Types ───────────────────────────────────────────────────────────────────

export type TakeawayPosition = 'head' | 'tail' | 'byTime';

export interface KdsCourseBucket {
  id: string;
  name: string;
  /** 0-based presentation order */
  order: number;
}

export interface KdsBoardConfig {
  version: 1;
  courseBuckets: KdsCourseBucket[];
  /** categoryId (DishCategory.id) -> bucket.id */
  categoryToBucket: Record<string, string>;
  takeawayPosition: TakeawayPosition;
  /** default 480 (8 min) */
  delayWarnSeconds: number;
  /** default 900 (15 min) */
  delayOverdueSeconds: number;
}

// ─── Default config ───────────────────────────────────────────────────────────

/**
 * The board used until the venue's own arrives (or when it has none), its
 * group names in the reader's language. The ids stay: they are what a saved
 * board's categoryToBucket points at.
 */
export function defaultKdsBoardConfig(t: SharedT): KdsBoardConfig {
  return {
    version: 1,
    courseBuckets: [
      // i18n-keep: bucket id stored in the venue's board config, never shown to anyone
      { id: 'default-antipasti', name: t('kds.board.course.antipasti', 'Antipasti'), order: 0 },
      // i18n-keep: bucket id stored in the venue's board config, never shown to anyone
      { id: 'default-primi', name: t('kds.board.course.primi', 'Primi'), order: 1 },
      // i18n-keep: bucket id stored in the venue's board config, never shown to anyone
      { id: 'default-secondi', name: t('kds.board.course.secondi', 'Secondi'), order: 2 },
      // i18n-keep: bucket id stored in the venue's board config, never shown to anyone
      { id: 'default-dolci', name: t('kds.board.course.dolci', 'Dolci'), order: 3 },
    ],
    categoryToBucket: {},
    takeawayPosition: 'tail',
    delayWarnSeconds: 480,
    delayOverdueSeconds: 900,
  };
}

/** The defaults with Italian names, for callers with no translator. */
export const DEFAULT_KDS_BOARD_CONFIG: KdsBoardConfig = defaultKdsBoardConfig(sourceT);

// ─── Shared helpers ───────────────────────────────────────────────────────────

/** True when an item should be shown as active (not SERVED, not CANCELLED). */
export function isItemActive(item: { status: string }): boolean {
  return item.status !== 'SERVED' && item.status !== 'CANCELLED';
}

/**
 * Riconoscimento "entré di benvenuto" PER NOME PIATTO (scelta esplicita utente
 * 2026-05-30). L'entré ha categoria "Antipasti" sul menu, quindi non è
 * distinguibile per categoria: lo individuiamo dal nome e lo forziamo in cima.
 * NOTA: hack volutamente specifico; follow-up = categoria "Entré" dedicata.
 */
const ENTREE_NAME_REGEX = /\bentr[éeè]\b|benvenut|amuse|couvert/i;

/** Estrae il nome del piatto da un item (vari shape possibili). */
export function itemDishName(item: any): string {
  return item?.dish?.name ?? item?.name ?? '';
}

/** True se l'item è l'entré di benvenuto (match per nome, vedi ENTREE_NAME_REGEX). */
export function isEntreeItem(item: any): boolean {
  return ENTREE_NAME_REGEX.test(itemDishName(item));
}

/** Bucket sintetico "Entré" — sempre primo (order minimo). */
const ENTREE_BUCKET_ID = '__entree__';

function entreeBucket(t: SharedT): KdsCourseBucket {
  return {
    id: ENTREE_BUCKET_ID,
    name: t('kds.table.entree', 'Entré'),
    order: Number.NEGATIVE_INFINITY,
  };
}

/** Estrae il categoryId da un item KitchenOrderItem (vari shape possibili). */
export function itemCategoryId(item: any): string | undefined {
  return (
    item?.dish?.categoryId ??
    item?.dish?.category?.id ??
    item?.categoryId ??
    item?.category?.id ??
    undefined
  );
}

/**
 * True se l'item è rilevante per la vista CUCINA: attivo (non SERVED/CANCELLED)
 * E la sua categoria è MAPPATA a un gruppo-portata nella config CRM.
 *
 * Niente regex: è il gestore che decide cosa va in cucina assegnando le
 * categorie ai gruppi-portata da /restaurant/settings/kds-board. Tutto ciò
 * che non è mappato (tipicamente bevande/bar) NON compare nella vista cuoco.
 */
export function isActiveKitchenItem(item: any, config: KdsBoardConfig): boolean {
  if (!isItemActive(item)) return false;
  const categoryId = itemCategoryId(item);
  if (!categoryId) return false;
  return config.categoryToBucket[categoryId] != null;
}

// ─── GroupOrdersByTable ───────────────────────────────────────────────────────

export interface TableGroup {
  /** Stable key: tableId for dine-in, "__takeaway__" for null-tableId orders. */
  key: string;
  tableName: string;
  isTakeaway: boolean;
  /** All non-completed, non-cancelled orders for this table. */
  orders: any[];
  /** Flat list of ALL items across all orders in this group (active + SERVED). */
  items: any[];
}

/**
 * Groups orders by tableId.
 * Orders with tableId == null aggregate into ONE "Asporto" group.
 * Orders with status COMPLETED or CANCELLED are excluded by the caller
 * (ChefOverview already filters these); this function passes them through as-is.
 */
export function groupOrdersByTable(
  orders: any[],
  _config: KdsBoardConfig,
  t: SharedT = sourceT,
): TableGroup[] {
  const map = new Map<string, TableGroup>();

  for (const order of orders) {
    if (order.tableId == null) {
      // Takeaway / counter / delivery — UNA card PER ORDINE (chiave = orderId),
      // etichettata "ASPORTO #<numero>" (+ pager se assegnato). Così nella vista
      // per-tavolo il cuoco vede ogni asporto come un "tavolo" a sé, con l'id
      // dell'ordine, invece di un unico blocco "Asporto" aggregato.
      const key = `__takeaway__${order.id}`;
      const num =
        order.dailyNumber != null
          ? String(order.dailyNumber)
          : String(order.id ?? '').slice(-4).toUpperCase();
      const tableName =
        order.pagerNumber != null
          ? t('kds.board.takeawayNumberedPager', 'ASPORTO #{{number}} · 📟 {{pager}}', {
              number: num,
              pager: order.pagerNumber,
            })
          : t('kds.board.takeawayNumbered', 'ASPORTO #{{number}}', { number: num });
      map.set(key, {
        key,
        tableName,
        isTakeaway: true,
        orders: [order],
        items: [...(order.items ?? [])],
      });
    } else {
      const tableKey = order.tableId as string;
      const tableName: string =
        order.tableName ?? t('kds.order.tableNumber', 'Tavolo {{number}}', { number: order.tableNumber });
      const existing = map.get(tableKey);
      if (existing) {
        existing.orders.push(order);
        existing.items.push(...(order.items ?? []));
      } else {
        map.set(tableKey, {
          key: tableKey,
          tableName,
          isTakeaway: false,
          orders: [order],
          items: [...(order.items ?? [])],
        });
      }
    }
  }

  return Array.from(map.values());
}

// ─── BucketItemsByCourse ──────────────────────────────────────────────────────

export interface CourseBucketGroup {
  bucket: KdsCourseBucket;
  items: any[];
}

/**
 * Buckets active kitchen items by course/category mapping.
 *
 * Rules:
 * - Only items whose categoryId is mapped in config.categoryToBucket are shown.
 *   Callers pre-filter with isActiveKitchenItem, but we defensively skip any
 *   item without a resolved bucket here too.
 * - isComped items are NOT excluded.
 * - Within each bucket, items are sorted by wait ASC (oldest firedAt??createdAt
 *   first — longest wait on top).
 * - Items with quantity > 1 stay as one row.
 * - Buckets are ordered by bucket.order ascending.
 * - Empty buckets are omitted from the result.
 */
export function bucketItemsByCourse(
  items: any[],
  config: KdsBoardConfig,
  t: SharedT = sourceT,
): CourseBucketGroup[] {
  const ENTREE_BUCKET = entreeBucket(t);
  // Build bucket index for fast lookup.
  const bucketById = new Map<string, KdsCourseBucket>();
  for (const b of config.courseBuckets) {
    bucketById.set(b.id, b);
  }

  // Assign each item to a mapped bucket; skip items with no mapping.
  const bucketItems = new Map<string, any[]>();

  for (const item of items) {
    // Entré di benvenuto (match per nome): va sempre nel bucket "Entré" in cima,
    // a prescindere dalla categoria (che è "Antipasti").
    if (isEntreeItem(item)) {
      bucketById.set(ENTREE_BUCKET.id, ENTREE_BUCKET);
      const ex = bucketItems.get(ENTREE_BUCKET.id);
      if (ex) ex.push(item);
      else bucketItems.set(ENTREE_BUCKET.id, [item]);
      continue;
    }

    const categoryId = itemCategoryId(item);
    if (!categoryId) continue;
    const bucketId = config.categoryToBucket[categoryId];
    if (!bucketId) continue; // unmapped — should not appear (filtered upstream)
    const bucket = bucketById.get(bucketId);
    if (!bucket) continue; // stale config reference — skip defensively

    const existing = bucketItems.get(bucket.id);
    if (existing) {
      existing.push(item);
    } else {
      bucketItems.set(bucket.id, [item]);
    }
  }

  if (bucketItems.size === 0) return [];

  // Sort within each bucket by wait ASC (oldest first = longest wait on top).
  // Tiebreaker su id (chiave immutabile, MAI lo stato) così a parità di attesa
  // la posizione resta fissa: un cambio di stato + refetch non riordina le righe.
  for (const [, arr] of bucketItems) {
    arr.sort((a: any, b: any) => {
      const ta = a.firedAt ? new Date(a.firedAt).getTime() : new Date(a.createdAt).getTime();
      const tb = b.firedAt ? new Date(b.firedAt).getTime() : new Date(b.createdAt).getTime();
      if (ta !== tb) return ta - tb; // ascending = oldest first (longest wait on top)
      return String(a.id).localeCompare(String(b.id));
    });
  }

  // Build result ordered by bucket.order. Il bucket sintetico "Entré" (se
  // presente) va sempre per primo; poi i bucket di config per ordine.
  const result: CourseBucketGroup[] = [];
  if (bucketItems.has(ENTREE_BUCKET.id)) {
    result.push({
      bucket: ENTREE_BUCKET,
      items: bucketItems.get(ENTREE_BUCKET.id)!,
    });
  }
  result.push(
    ...[...config.courseBuckets]
      .filter((b) => bucketItems.has(b.id))
      .sort((a, b) => a.order - b.order)
      .map((bucket) => ({ bucket, items: bucketItems.get(bucket.id)! })),
  );

  return result;
}

// ─── BucketItemsByWave (feature 097 — ondate come raggruppamento primario) ─────

export interface WaveGroup {
  /** Valore effettivo d'ondata (serviceWave ?? courseNumber ?? 1). Per l'ordinamento. */
  wave: number;
  /** Indice 0-based contiguo → etichetta progressiva ("1ª ondata", "2ª ondata", …). */
  positionIndex: number;
  /** Timestamp (ms) del piatto FIRATO più vecchio dell'ondata (min firedAt).
   *  null se l'ondata non è ancora stata chiamata (nessun item firato):
   *  il cronometro NON deve correre prima dell'invio in cucina. */
  oldestTs: number | null;
  /** Sotto-bucket per categoria, prodotti da bucketItemsByCourse() sugli item dell'ondata. */
  courseBuckets: CourseBucketGroup[];
}

export interface WaveBucketResult {
  /** Blocco entré dedicato (se presente), mostrato SOPRA le ondate. null se nessun entré. */
  entree: CourseBucketGroup | null;
  /** Ondate ordinate per valore crescente, etichettate progressivamente per posizione. */
  waves: WaveGroup[];
}

/** Ondata effettiva di un item: serviceWave ?? courseNumber ?? 1 (sempre definita). */
export function effectiveWave(item: any): number {
  return item?.serviceWave ?? item?.courseNumber ?? 1;
}

/**
 * Etichetta progressiva PER POSIZIONE (FR-004): 0 → "1ª ondata", 1 → "2ª ondata", …
 * Indipendente dal valore grezzo di serviceWave (numeri non contigui non generano buchi).
 * La resa MAIUSCOLA è applicata via CSS nel componente, non qui.
 */
export function waveLabelForIndex(index: number, t: SharedT = sourceT): string {
  return t('kds.wave.ordinalLabel', '{{position}}ª ondata', { position: index + 1 });
}

/** Formatta millisecondi trascorsi in "mm:ss" (o "h:mm:ss" oltre l'ora). Null-safe su negativi. */
export function formatElapsed(ms: number): string {
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Raggruppa gli item per ONDATA come livello primario, con la categoria come
 * sotto-bucket dentro ciascuna ondata (feature 097).
 *
 * Regole (vedi contracts/wave-grouping.md):
 * - Gli item entré (match per nome) sono estratti PRIMA della partizione e
 *   restituiti come blocco "Entré" dedicato sopra le ondate (FR-014).
 * - I restanti sono partizionati per effectiveWave; le ondate sono ordinate per
 *   valore crescente e ricevono un positionIndex 0-based contiguo (FR-004).
 * - Per ogni ondata i sotto-bucket categoria sono prodotti da bucketItemsByCourse
 *   (eredita: mapping categoria→bucket, ordine bucket, sort interno per attesa,
 *   skip non-mappati). Gli item entré sono già stati rimossi, quindi qui non
 *   ricompaiono.
 * - oldestTs = min(firedAt) sui SOLI piatti firati dell'ondata; null se
 *   l'ondata non è stata ancora chiamata (timer fermo, FR-007 + fix timer
 *   ondate non chiamate 2026-06-11).
 * - Le ondate senza item mappati non compaiono (no blocchi vuoti, FR-012).
 */
export function bucketItemsByWave(
  items: any[],
  config: KdsBoardConfig,
  t: SharedT = sourceT,
): WaveBucketResult {
  // 0. Difensivo: scarta item non attivi (SERVED/CANCELLED). In produzione il
  //    caller passa già activeItems, ma così la funzione resta corretta anche
  //    standalone (INV-4).
  const active = items.filter(isItemActive);

  // 1. Estrai gli item entré (per nome) → blocco dedicato sopra le ondate.
  const entreeItems: any[] = [];
  const rest: any[] = [];
  for (const item of active) {
    if (isEntreeItem(item)) entreeItems.push(item);
    else rest.push(item);
  }

  let entree: CourseBucketGroup | null = null;
  if (entreeItems.length > 0) {
    // bucketItemsByCourse mette tutti gli entré nel bucket sintetico "Entré" (primo).
    const entreeGroups = bucketItemsByCourse(entreeItems, config, t);
    entree = entreeGroups.length > 0 ? entreeGroups[0] : null;
  }

  // 2. Partiziona i restanti per ondata effettiva.
  const byWave = new Map<number, any[]>();
  for (const item of rest) {
    const w = effectiveWave(item);
    const arr = byWave.get(w);
    if (arr) arr.push(item);
    else byWave.set(w, [item]);
  }

  // 3. Ordina le ondate per valore crescente, costruisci i blocchi.
  const sortedWaveKeys = [...byWave.keys()].sort((a, b) => a - b);
  const waves: WaveGroup[] = [];
  let positionIndex = 0;
  for (const w of sortedWaveKeys) {
    const waveItems = byWave.get(w)!;
    const courseBuckets = bucketItemsByCourse(waveItems, config, t);
    // Ondata senza item mappati → niente blocco (no blocchi vuoti).
    if (courseBuckets.length === 0) continue;

    // oldestTs sui SOLI piatti firati: un'ondata non ancora chiamata non ha
    // cronometro (il tempo parte da firedAt, mai da createdAt).
    let oldest = Infinity;
    for (const cb of courseBuckets) {
      for (const it of cb.items) {
        if (!it.firedAt) continue;
        const ts = new Date(it.firedAt).getTime();
        if (Number.isFinite(ts) && ts < oldest) oldest = ts;
      }
    }

    waves.push({
      wave: w,
      positionIndex,
      oldestTs: Number.isFinite(oldest) ? oldest : null,
      courseBuckets,
    });
    positionIndex++;
  }

  return { entree, waves };
}

// ─── Wait calculation helpers ─────────────────────────────────────────────────

/**
 * Wait in ms for a table group: elapsed since the oldest FIRED active kitchen
 * item (oldest firedAt = longest actually in the kitchen queue). Gli item di
 * ondate non ancora chiamate non contano: non sono in lavorazione.
 * Items not mapped in config are ignored.
 */
export function tableWaitMs(group: TableGroup, now: number, config: KdsBoardConfig): number {
  let oldest = Infinity;
  for (const item of group.items) {
    if (!isActiveKitchenItem(item, config)) continue;
    if (!item.firedAt) continue;
    const ts = new Date(item.firedAt).getTime();
    if (ts < oldest) oldest = ts;
  }
  if (!isFinite(oldest)) return 0;
  return now - oldest;
}

/**
 * Sorts TableGroup array: dine-in groups by tableWaitMs desc.
 * Asporto position controlled by config.takeawayPosition.
 */
export function sortTables(
  groups: TableGroup[],
  config: KdsBoardConfig,
  now: number,
): TableGroup[] {
  const byWaitDesc = (a: TableGroup, b: TableGroup) =>
    tableWaitMs(b, now, config) - tableWaitMs(a, now, config);
  // Ora ogni asporto è un gruppo a sé (chiave = orderId): ne gestiamo N, non 1.
  const takeaways = groups.filter((g) => g.isTakeaway).sort(byWaitDesc);
  const dineIn = groups.filter((g) => !g.isTakeaway).sort(byWaitDesc);

  if (takeaways.length === 0) return dineIn;

  switch (config.takeawayPosition) {
    case 'head':
      return [...takeaways, ...dineIn];
    case 'tail':
      return [...dineIn, ...takeaways];
    case 'byTime':
      // Interleave asporti e tavoli per attesa decrescente.
      return [...dineIn, ...takeaways].sort(byWaitDesc);
  }
}

// ─── Delay tier ───────────────────────────────────────────────────────────────

export type DelayTier = 'normal' | 'warn' | 'overdue';

export function delayTier(
  item: { firedAt?: string | null; createdAt: string },
  now: number,
  config: KdsBoardConfig,
): DelayTier {
  // Ondata non ancora chiamata: nessun ritardo possibile (il tempo di
  // cucina parte da firedAt, non dalla creazione dell'ordine).
  if (!item.firedAt) return 'normal';
  const ts = new Date(item.firedAt).getTime();
  const elapsedSeconds = (now - ts) / 1000;
  if (elapsedSeconds >= config.delayOverdueSeconds) return 'overdue';
  if (elapsedSeconds >= config.delayWarnSeconds) return 'warn';
  return 'normal';
}

/**
 * Returns the id of the single oldest active kitchen item across all table groups.
 * Used to render the "più vecchio" badge (FR-015 — seam for US4).
 * Items not mapped in config are ignored.
 */
export function oldestActiveItemId(
  groups: TableGroup[],
  _now: number,
  config: KdsBoardConfig,
): string | null {
  let oldestTs = Infinity;
  let oldestId: string | null = null;

  for (const group of groups) {
    for (const item of group.items) {
      if (!isActiveKitchenItem(item, config)) continue;
      if (!item.firedAt) continue;
      const ts = new Date(item.firedAt).getTime();
      if (ts < oldestTs) {
        oldestTs = ts;
        oldestId = item.id;
      }
    }
  }

  return oldestId;
}

// ─── Station filter (single-station per-table board, e.g. drinks monitor) ────

/** Bucket id used when the board is scoped to one station. */
export const STATION_BUCKET_ID = 'station';

/**
 * Keep only the items routed to `stationId` (KitchenOrderItem.stationId) and
 * drop the orders left without any. Items never mapped to a station
 * (stationId null) are excluded on purpose.
 */
export function filterOrdersByStation(orders: any[], stationId: string): any[] {
  const out: any[] = [];
  for (const order of orders ?? []) {
    const items = (order.items ?? []).filter((i: any) => i?.stationId === stationId);
    if (items.length > 0) out.push({ ...order, items });
  }
  return out;
}

/**
 * Board config for a board scoped to one station: every category seen on the
 * station's items maps to a single bucket, so the client's kitchen mapping
 * (which deliberately leaves the bar out) cannot hide them. Delay thresholds
 * and takeaway position come from the client config unchanged.
 */
export function stationBoardConfig(
  base: KdsBoardConfig,
  orders: any[],
  bucketName: string,
): KdsBoardConfig {
  const categoryToBucket: Record<string, string> = {};
  for (const order of orders ?? []) {
    for (const item of order.items ?? []) {
      const categoryId = itemCategoryId(item);
      if (categoryId) categoryToBucket[categoryId] = STATION_BUCKET_ID;
    }
  }
  return {
    ...base,
    courseBuckets: [{ id: STATION_BUCKET_ID, name: bucketName, order: 0 }],
    categoryToBucket,
  };
}

/**
 * Items sorted by wait, oldest first (firedAt, else createdAt). Used by the
 * flat single-station board (bar), where waves and course buckets have no
 * meaning and the card is one plain list.
 */
export function sortItemsByWait(items: any[]): any[] {
  const ts = (i: any) =>
    i?.firedAt ? new Date(i.firedAt).getTime() : new Date(i?.createdAt).getTime();
  return [...(items ?? [])].sort((a, b) => ts(a) - ts(b));
}
